import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { connect } from 'react-redux';
import api from '../api';
import Page from '../components/Page';
import withLicenseMsgModal from '../components/withLicenseMsgModal';
import InvitationCardBase from '../components/InvitationCardBase';
import history from '../history';
import { nsOptions } from '../i18n';
import Toast from '../utils/Toast';
import ErrorUtil from '../utils/ErrorUtil';
import UserUtil from '../utils/UserUtil';
import { TEAM_TYPE_ORGANIZATION, INSTITUTION_TYPES } from '../constants';
import { authActions } from '../redux/actions';

const mapStateToProps = (state) => ({
  user: state.auth.authUser,
});

const mapDispatchToProps = (dispatch) => ({
  refreshUser: () => dispatch(authActions.tryAutoReAuth()),
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
@withLicenseMsgModal()
class JoinTeam extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    match: PropTypes.shape().isRequired,
    user: PropTypes.shape().isRequired,
    refreshUser: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props, props.t('common:nav.join-project'));
    this.state = { invitation: null, errorMessage: '', ready: false };
  }

  componentDidMount() {
    this.fetchInvitation();
  }

  fetchInvitation = async () => {
    const { token } = this.props.match.params;
    try {
      const invitation = await api.read('confirm-team-invitation', token);
      this.setState({ invitation });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error, false);
      const errorMessage = ErrorUtil.makeApiErrorUserFriendly(this.props, error.message);
      this.setState({ invitation: null, errorMessage });
    } finally {
      this.setState({ ready: true });
    }
  };

  join = async () => {
    try {
      const { user, refreshUser } = this.props;
      const { token } = this.props.match.params;
      await api.create(`confirm-team-invitation/${token}`);
      const { invitation } = this.state;

      // For organization teams (admin teams), redirect to the appropriate page
      // and refresh user to update navigation permissions
      if (invitation.team.type === TEAM_TYPE_ORGANIZATION) {
        Toast.success(this.props, 'error:valid.success');
        await refreshUser();
        // Institution users (owners) go to their admin team settings
        // Regular users go to organization teams page with the team preselected
        const redirectUrl = INSTITUTION_TYPES.includes(user.type)
          ? '/dashboard/settings/admin-team'
          : `/dashboard/settings/teams/org-teams/${invitation.team.id}`;
        history.push(redirectUrl);
        return;
      }

      let url = `/dashboard/settings/teams/${invitation.team.id}`;
      if (user && invitation
        && !INSTITUTION_TYPES.includes(user.type)
        && user.id !== invitation.team.owner.id) {
        url = `/dashboard/settings/teams/joined-teams/${invitation.team.id}`;
      }
      history.push(url);
      Toast.success(this.props, 'error:valid.success');
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  cancel = async () => {
    try {
      const { token } = this.props.match.params;
      await api.create(`dismiss-team-invitation/${token}`);
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }

    const { user } = this.props;
    const homeLink = UserUtil.getUserHome(user);
    history.push(`/dashboard/${homeLink}`);
  };

  render() {
    const { t } = this.props;
    const { invitation, errorMessage, ready } = this.state;
    const user = invitation ? invitation.creator : null;
    const team = invitation ? invitation.team : null;

    return (
      <Page
        {...this.props}
        className="dashboard-content"
        title={t('common:nav.join-project')}
      >
        <InvitationCardBase
          invitation={invitation}
          errorMessage={errorMessage}
          ready={ready}
          label={user ? user.label : ''}
          join={this.join}
          cancel={this.cancel}
          title="user:team.invitation-card.title"
          subtitle="user:team.invitation-card.subtitle"
          joinLabel="user:team.invitation-card.join-label"
          invalidInvitationMsg="user:team.invitation-card.invalid-invitation-msg"
        >
          {
            team && (
              <>
                <div>
                  <span className="mr-1 font-weight-semibold">{t('user:team.invitation-card.team-name')}</span>
                  <span>{team.name}</span>
                </div>
                <div>
                  <span className="mr-1 font-weight-semibold">{t('user:team.invitation-card.owner')}</span>
                  <span>{team.owner?.institution || team.owner?.label}</span>
                </div>
              </>
            )
          }
        </InvitationCardBase>
      </Page>
    );
  }
}

export default JoinTeam;
